import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse,
  faCircleExclamation,
  faClipboardList,
  faCalendarAlt,
  faUsers,
  faFileShield,
  faFileAlt,
  faRobot,
  faPhone,
  faBriefcase,
  faChartBar,
  faChartLine,
  faCreditCard,
  faPuzzlePiece,
  faCog,
  faLifeRing,
  faChevronDown,
  faChevronUp,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "./Sidebar.css";
import { useAiAssistantUnreadCount } from "../../hooks/useAiAssistantUnreadCount";
import { useShiftReportUnreadCount } from "../../hooks/useShiftReportUnreadCount";
import { useSupportTicketOpenCount } from "../../hooks/useSupportTicketOpenCount";
import { useCareHubData } from "../../hooks/useCareHubData";
import { countActiveAssignmentCases } from "../../utils/careTriageModel";

const SECTION_STATE_KEY = "albert.sidebar.sections";

// Route prefixes per collapsible section — the section holding the current
// page opens automatically until the user toggles it themselves.
const SECTION_PATHS = {
  care: ["/client-management", "/appointments", "/compliance-management", "/albertai-alerts"],
  ai: ["/agent247", "/ai-kathy", "/ai-hiring", "/aip-smart-care", "/ask-albert"],
  insights: ["/analytics"],
  admin: [
    "/wellsky-sync",
    "/payment-billing",
    "/support-tickets",
    "/admin/",
    "/question-sets",
  ],
};

const Sidebar = ({
  wellSkyReportVisible = false,
  // Feature-scoped access ("Agent247 Only" / "AI Hiring Only" restricted
  // users, and legacy Agency.aiHiringOnly folded in by App.js). Non-empty =
  // show ONLY the nav item(s) for these features, nothing else — no
  // Dashboard, no other sections, no Help Center. Empty/absent = normal,
  // unrestricted full sidebar (current behavior for every existing user).
  restrictedFeatures = [],
  collapsed = false,
  onToggleCollapse,
  activeLocation,
  locations = [],
  currentUser,
}) => {
  const isRestricted = restrictedFeatures.length > 0;
  const location = useLocation();
  const [integrationOpen, setIntegrationOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aiHiringOpen, setAiHiringOpen] = useState(false);
  const [sectionOpen, setSectionOpen] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(SECTION_STATE_KEY)) || {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(SECTION_STATE_KEY, JSON.stringify(sectionOpen));
    } catch {
      /* storage unavailable */
    }
  }, [sectionOpen]);
  const aiCallAssistUnreadCount = useAiAssistantUnreadCount(activeLocation, currentUser);
  const shiftReportUnreadCount = useShiftReportUnreadCount(activeLocation);
  const { badgeCount: supportTicketBadgeCount } = useSupportTicketOpenCount();
  const { summary, triageCases } = useCareHubData(activeLocation, locations);
  const triageBadgeCount = summary?.action?.total || 0;
  const assignedBadgeCount = countActiveAssignmentCases(triageCases).total;

  const isActive = (path, exact = false) => {
    if (exact || path === "/" || path === "/dashboard") {
      return (
        location.pathname === "/" ||
        location.pathname === "/dashboard" ||
        location.pathname === path
      );
    }
    return location.pathname.startsWith(path);
  };

  const isSettingsActive = () =>
    [
      "/admin/locations",
      "/admin/users",
      "/admin/caregivers",
      "/admin/nurses",
      "/question-sets",
      "/admin/terms-of-use",
      "/admin/privacy-policy",
    ].some((p) => location.pathname.startsWith(p));

  const isIntegrationActive = () =>
    location.pathname.startsWith("/wellsky-sync");

  const isAiHiringActive = () => location.pathname.startsWith("/ai-hiring");

  // Live, routed AI Hiring sub-pages only (App.js Routes) — keep in sync with
  // that route list; /ai-hiring/questions is an alias of Settings, and
  // Profile/Reports/Questions components exist but aren't mounted as routes.
  const AI_HIRING_SUBNAV_ITEMS = [
    { label: "Overview",          path: "/ai-hiring/overview" },
    { label: "Candidates",        path: "/ai-hiring/candidates" },
    { label: "Drip Campaigns",    path: "/ai-hiring/drip-campaigns" },
    { label: "Review Queue",      path: "/ai-hiring/review-queue" },
    { label: "Interview Settings", path: "/ai-hiring/settings" },
  ];

  /** Renders the expandable "AI Hiring" nav item + its subnav list */
  const AiHiringNavItem = () => (
    <li className="sidebar-nav-item">
      <button
        type="button"
        className={`sidebar-nav-link sidebar-nav-expandable${
          isAiHiringActive() ? " active" : ""
        }${aiHiringOpen ? " expanded" : ""}`}
        onClick={() => setAiHiringOpen((o) => !o)}
        aria-expanded={aiHiringOpen}
        title={collapsed ? "AI Hiring" : undefined}
      >
        <span className="sidebar-nav-icon">
          <FontAwesomeIcon icon={faBriefcase} />
        </span>
        {!collapsed && (
          <>
            <span className="sidebar-nav-label">AI Hiring</span>
            <FontAwesomeIcon
              icon={aiHiringOpen ? faChevronUp : faChevronDown}
              className="sidebar-nav-caret"
            />
          </>
        )}
      </button>
      {aiHiringOpen && !collapsed && (
        <ul className="sidebar-subnav-list">
          {AI_HIRING_SUBNAV_ITEMS.map(({ label, path }) => (
            <li key={path} className="sidebar-subnav-item">
              <Link
                to={path}
                className={`sidebar-subnav-link${
                  location.pathname.startsWith(path) ? " active" : ""
                }`}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );

  /** Renders a standard nav link <li> */
  const NavItem = ({
    label,
    path,
    icon,
    exact = false,
    badge = 0,
    badgeVariant = "default",
    activeOverride,
  }) => {
    const active = (() => {
      if (activeOverride !== undefined) return activeOverride;
      if (label === "Dashboard") {
        return location.pathname === "/care-dashboard";
      }
      if (label === "AI Hiring") return location.pathname.startsWith("/ai-hiring");
      return exact ? isActive(path, true) : isActive(path);
    })();
    return (
      <li className="sidebar-nav-item">
        <Link
          to={path}
          className={`sidebar-nav-link${active ? " active" : ""}`}
          title={
            collapsed
              ? badge > 0
                ? `${label} (${badge} unread)`
                : label
              : undefined
          }
        >
          <span className="sidebar-nav-icon">
            <FontAwesomeIcon icon={icon} />
          </span>
          {!collapsed && <span className="sidebar-nav-label">{label}</span>}
          {badge > 0 && (
            <span
              className={`sidebar-nav-badge${
                badgeVariant === "alert" ? " sidebar-nav-badge--alert" : ""
              }`}
            >
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </Link>
      </li>
    );
  };

  /**
   * Collapsible section. In icon-only mode the header is hidden and every
   * item stays visible, since there is no label to click.
   */
  const NavSection = ({ id, label, badge = 0, children }) => {
    if (collapsed) return children;
    const holdsActive = SECTION_PATHS[id].some((p) => location.pathname.startsWith(p));
    const open = sectionOpen[id] ?? holdsActive;
    return (
      <>
        <li className="sidebar-section-header">
          <button
            type="button"
            className="sidebar-section-toggle"
            onClick={() => setSectionOpen((prev) => ({ ...prev, [id]: !open }))}
            aria-expanded={open}
          >
            <span>{label}</span>
            {!open && badge > 0 ? (
              <span className="sidebar-nav-badge sidebar-section-badge">
                {badge > 99 ? "99+" : badge}
              </span>
            ) : null}
            <FontAwesomeIcon
              icon={open ? faChevronUp : faChevronDown}
              className="sidebar-section-caret"
            />
          </button>
        </li>
        {open ? children : null}
      </>
    );
  };

  return (
    <aside
      className={`app-sidebar${collapsed ? " sidebar-collapsed" : ""}`}
      aria-label="Main navigation"
    >
      <div className="sidebar-inner">
        {/* Logo — only when expanded */}
        {!collapsed && (
          <div className="sidebar-logo">
            <img
              src="/Combo_PartialColor.png"
              alt="Unity Global Care | Albert AI"
              className="sidebar-logo-img"
            />
          </div>
        )}

        {/* Collapse toggle */}
        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronLeft} />
        </button>

        <nav className="sidebar-nav" aria-label="Primary navigation">
          <ul className="sidebar-nav-list">

            {/* ── Feature-restricted mode: ONLY the permitted feature item(s),
                 nothing else (no Dashboard, no other sections) ── */}
            {isRestricted && (
              <>
                {restrictedFeatures.includes("agent247") && (
                  <NavItem
                    label="AI Call Assist"
                    path="/agent247"
                    icon={faPhone}
                    badge={aiCallAssistUnreadCount}
                  />
                )}
                {restrictedFeatures.includes("ai_hiring") && <AiHiringNavItem />}
                <NavItem label="Support Tickets" path="/support-tickets" icon={faLifeRing} badge={supportTicketBadgeCount} />
              </>
            )}

            {/* ── Dashboard — pinned, no section header ── */}
            {!isRestricted && (
              <>
                <NavItem
                  label="Dashboard"
                  path="/care-dashboard"
                  icon={faHouse}
                  exact
                />
                <NavItem
                  label="Triage"
                  path="/care/triage?bucket=action&tab=new"
                  icon={faCircleExclamation}
                  badge={triageBadgeCount}
                  badgeVariant="alert"
                  activeOverride={location.pathname.startsWith("/care/triage")}
                />
                <NavItem
                  label="Assigned Cases"
                  path="/care/assigned-cases"
                  icon={faClipboardList}
                  badge={assignedBadgeCount}
                  badgeVariant="alert"
                  activeOverride={location.pathname.startsWith("/care/assigned-cases")}
                />
              </>
            )}

            {!isRestricted && (
              <>
                {/* ══ CARE OPERATIONS ══ */}
                <NavSection id="care" label="CARE OPERATIONS" badge={shiftReportUnreadCount}>

                {/* A-Z: Client Mgmt, Client Scheduling, Compliance Mgmt, Shift Report */}
                <NavItem label="Client Mgmt"       path="/client-management"    icon={faUsers} />
                <NavItem label="Client Scheduling" path="/appointments"          icon={faCalendarAlt} />
                <NavItem label="Compliance Mgmt"   path="/compliance-management" icon={faFileShield} />
                <NavItem label="Shift Report"       path="/albertai-alerts"       icon={faFileAlt} badge={shiftReportUnreadCount} />
                </NavSection>

                {/* ══ AI TOOLS ══ */}
                <NavSection id="ai" label="AI TOOLS" badge={aiCallAssistUnreadCount}>

                {/* A-Z: AI Call Assist, AI Hiring, AI Kathy, AIP Care Monitoring, Ask Albert */}
                <NavItem label="AI Call Assist"      path="/agent247"           icon={faPhone} badge={aiCallAssistUnreadCount} />
                <NavItem label="AI Kathy"            path="/ai-kathy"            icon={faRobot} />
                <AiHiringNavItem />
                <NavItem label="Simulation" path="/aip-smart-care" icon={faChartLine} />
                <NavItem label="Ask Albert"          path="/ask-albert"         icon={faRobot} />
                </NavSection>

                {/* ══ INSIGHTS ══ */}
                <NavSection id="insights" label="INSIGHTS">
                <NavItem label="Analytics & Reports" path="/analytics" icon={faChartBar} />
                </NavSection>

                {/* ══ ADMINISTRATION ══ */}
                <NavSection id="admin" label="ADMINISTRATION" badge={supportTicketBadgeCount}>

                {/* Integration expandable (A-Z: comes before Payment & Billing, Settings) */}
                <li className="sidebar-nav-item">
                  <button
                    type="button"
                    className={`sidebar-nav-link sidebar-nav-expandable${
                      isIntegrationActive() ? " active" : ""
                    }${integrationOpen ? " expanded" : ""}`}
                    onClick={() => setIntegrationOpen((o) => !o)}
                    aria-expanded={integrationOpen}
                    title={collapsed ? "Integration" : undefined}
                  >
                    <span className="sidebar-nav-icon">
                      <FontAwesomeIcon icon={faPuzzlePiece} />
                    </span>
                    {!collapsed && (
                      <>
                        <span className="sidebar-nav-label">Integration</span>
                        <FontAwesomeIcon
                          icon={integrationOpen ? faChevronUp : faChevronDown}
                          className="sidebar-nav-caret"
                        />
                      </>
                    )}
                  </button>
                  {integrationOpen && !collapsed && (
                    <ul className="sidebar-subnav-list">
                      {wellSkyReportVisible ? (
                        <li className="sidebar-subnav-item">
                          <Link
                            to="/wellsky-sync"
                            className={`sidebar-subnav-link${
                              location.pathname.startsWith("/wellsky-sync") ? " active" : ""
                            }`}
                          >
                            WellSky Sync &amp; Reports
                          </Link>
                        </li>
                      ) : (
                        <li className="sidebar-subnav-item sidebar-subnav-item--empty">
                          <span className="sidebar-subnav-link sidebar-subnav-empty-text">
                            No integrations active
                          </span>
                        </li>
                      )}
                    </ul>
                  )}
                </li>

                {/* Payment & Billing */}
                <NavItem label="Payment & Billing" path="/payment-billing" icon={faCreditCard} />
                <NavItem label="Support Tickets" path="/support-tickets" icon={faLifeRing} badge={supportTicketBadgeCount} />

                {/* Settings expandable */}
                <li className="sidebar-nav-item">
                  <button
                    type="button"
                    className={`sidebar-nav-link sidebar-nav-expandable${
                      isSettingsActive() ? " active" : ""
                    }${settingsOpen ? " expanded" : ""}`}
                    onClick={() => setSettingsOpen((o) => !o)}
                    aria-expanded={settingsOpen}
                    title={collapsed ? "Settings" : undefined}
                  >
                    <span className="sidebar-nav-icon">
                      <FontAwesomeIcon icon={faCog} />
                    </span>
                    {!collapsed && (
                      <>
                        <span className="sidebar-nav-label">Settings</span>
                        <FontAwesomeIcon
                          icon={settingsOpen ? faChevronUp : faChevronDown}
                          className="sidebar-nav-caret"
                        />
                      </>
                    )}
                  </button>
                  {settingsOpen && !collapsed && (
                    <ul className="sidebar-subnav-list">
                      {/* A-Z: Caregivers, Locations, Nurses, Privacy Policy, Question Sets,
                              Terms of Use, Users */}
                      {[
                        { label: "Users",          path: "/admin/users" },
                        { label: "Caregivers",     path: "/admin/caregivers" },
                        { label: "Nurses",         path: "/admin/nurses" },
                        { label: "Locations",      path: "/admin/locations" },
                        { label: "Question Sets",  path: "/question-sets"  },
                        { label: "Privacy Policy", path: "/admin/privacy-policy" },
                        { label: "Terms of Use",   path: "/admin/terms-of-use" },
                       
                      ].map(({ label, path }) => (
                        <li key={path} className="sidebar-subnav-item">
                          <Link
                            to={path}
                            className={`sidebar-subnav-link${
                              location.pathname.startsWith(path) ? " active" : ""
                            }`}
                          >
                            {label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
                </NavSection>
              </>
            )}

          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
